import React from "react";
import { FormProvider } from "react-hook-form";
import { Button, Dialog, Form } from "@sscs/ui";
import { useEditPolicyForm } from "../../hooks";
import { Policy, PolicyOverride } from "../../types";
import styles from "./editPolicy.module.scss";
import EditReason from "./EditReason";
import IsBlocker from "./IsBlocker";
import IsEnabled from "./IsEnabled";

type EditPolicyProps = {
  onClose: () => void;
  isOpen: boolean;
  policy: Policy;
  policyOverrideState: PolicyOverride;
  derivedScanLevel: number;
};

const getRevertInstruction = ({
  isTryingToUndoToOverride,
  isTryingToUndo,
  isTryingToRevert,
}: {
  isTryingToUndoToOverride: boolean;
  isTryingToUndo: boolean;
  isTryingToRevert: boolean;
}): string | null => {
  if (isTryingToUndoToOverride) {
    return "These settings match the last saved configuration for this policy, so there is nothing to apply. Close this dialog to keep it.";
  }

  if (isTryingToUndo || isTryingToRevert) {
    return `It looks like you are trying to revert policy configuration to the inherited one. To do that, close this dialog and use ${
      isTryingToRevert ? "Revert override" : "Undo edit"
    } action in the Actions menu.`;
  }

  return null;
};

const EditPolicyContent = ({
  onClose,
  policy,
  policyOverrideState,
  derivedScanLevel,
}: Omit<EditPolicyProps, "isOpen">): React.JSX.Element => {
  const {
    onSubmit,
    methods,
    inheritedBlocker,
    inheritedEnabled,
    isReasonFieldDisabled,
    isConfirmButtonDisabled,
    isDisabledPolicyModified,
    isTryingToUndo,
    isTryingToUndoToOverride,
    isTryingToRevert,
    reasons,
  } = useEditPolicyForm({
    policy,
    policyOverrideState,
    derivedScanLevel,
    onClose,
  });

  return (
    <FormProvider {...methods}>
      <Form.Form onSubmit={onSubmit}>
        <Dialog.Header>
          <Dialog.Header.Title>Policy configuration change</Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Section>
            <Dialog.Section.Description>
              You are about to change policy configuration settings for:{" "}
              <span className={styles["change-target"]}>
                {`${policy.id.toUpperCase()} - ${policy.description}`}
              </span>
            </Dialog.Section.Description>
            <Dialog.Section.Description>
              Inherited values for this policy are{" "}
              {inheritedEnabled ? "Enabled" : "Disabled"} and{" "}
              {inheritedBlocker ? "Fail" : "Pass"}.
            </Dialog.Section.Description>
            <Dialog.Section.Content>
              <IsEnabled />
              <IsBlocker />
              <EditReason
                isDisabled={isReasonFieldDisabled}
                reasons={reasons}
              />
              <div className={styles["revert-instructions"]}>
                {getRevertInstruction({
                  isTryingToUndoToOverride,
                  isTryingToUndo,
                  isTryingToRevert,
                })}
                {isDisabledPolicyModified &&
                !isTryingToRevert &&
                !isTryingToUndo
                  ? "Changing CI/CD status of disabled policy has no effect on analysis."
                  : null}
              </div>
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={isConfirmButtonDisabled}
            >
              Confirm
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const EditPolicy = ({
  isOpen,
  ...props
}: EditPolicyProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <EditPolicyContent {...props} />
  </Dialog.Frame>
);

export default EditPolicy;
